import {OperationModesEnum}                       from '../../../stores/common/operation-modes.store';
import {Injectable}                               from '@angular/core';
import {FieldCropRotationHistory}                 from '../../../stores/farm/crop.rotation.store';
import {ApplicationModesEnum}                     from '../../../stores/common/appl-modes.store';
import {INFertilizationPlanFieldToSelectGridItem} from '../types/n-fertilization-planning-entry.types';
import {GetRoundNumericService}                   from '../../../ap-utils/service/get-round-numeric.service';
import {ApColumnState}                            from '../../../ap-interface/enums/ap-column-state.enum';
import IGuid = System.IGuid;
import ICampaignYear = Data.Authentication.ICampaignYear;
import INFertilizationPlanBook = Data.TaskManagement.INFertilizationPlanBook;
import {FarmCropV2CrStore}                        from '../../../stores/base-data/farm.cropv2cr.store';
import {CropV2CrStore}                            from '../../../stores/base-data/cropv2cr.store';

@Injectable()
export class NFertilizationCommonEntryService {
  constructor(private farmCropV2CrStore: FarmCropV2CrStore,
              private cropV2CrStore: CropV2CrStore,
              private roundNumericService: GetRoundNumericService) {
  }

  // TODO: Remove during refactoring of n-fertilization planning
  /**
   * Determines if the provided operation mode key corresponds to one of the specified
   * operation mode group maps and is not a constant.
   * @param {OperationModesEnum} operationModeKey - The key representing the operation mode to evaluate.
   * @return {boolean} - Returns true if the operation mode key matches one of the specific group maps; otherwise, false.
   * @public
   */
  public isOperationModeGroupMapButNotConst(operationModeKey: OperationModesEnum): boolean {
    if (!operationModeKey) {
      return false;
    }
    return operationModeKey === OperationModesEnum.ApplicationMap
      || operationModeKey === OperationModesEnum.ApplicationMapAbsolute
      || operationModeKey === OperationModesEnum.NfTargetMapPerPlan;
  }

  /**
   * Calculates the yield expectation based on the operation mode and input value.
   * @param operationModeKey - The key representing the current operation mode.
   * @param yieldExpectationFromForm - An optional number representing the yield expectation from the form.
   * @return The calculated yield expectation, or `undefined` if not applicable.
   * @public
   */
  public getYieldExpectation(operationModeKey: OperationModesEnum, yieldExpectationFromForm?: number): number | undefined {
    if (operationModeKey === OperationModesEnum.RapeFertilization && !Number.isFinite(yieldExpectationFromForm)) {
      return 4;
    }
    return yieldExpectationFromForm;
  }

  /**
   * Calculates the minimum average nitrogen uptake value (NUptakeAvg) from the provided list of selected fields,
   * specifically for fields where the scan data status is green.
   * @param selectedFields - An array of fields to process. Each field
   * contains scan data with corresponding nitrogen uptake average and status properties.
   * @return Returns the minimum average nitrogen uptake (NUptakeAvg) value for fields with a green
   * scan data status, or undefined if no valid values are found in the input.
   * @public
   */
  public getAverageMinFromScans(selectedFields: INFertilizationPlanFieldToSelectGridItem[]): number | undefined {
    if (!selectedFields || selectedFields.length === 0) {
      return undefined;
    }
    return selectedFields.filter(f => f.ScanData?.Status?.state === ApColumnState.Green)
      .map(f => f.ScanData.NUptakeAvg)
      .filter(x => Number.isFinite(x))
      .reduce((min, current) => (!Number.isFinite(min) || current < min) ? current : min, undefined);
  }

  /**
   * Takes the minimum growth stage value (EC) from the provided list of selected fields,
   * specifically for fields where the scan data status is green.
   * @param selectedFields - An array of fields to process. Each field
   * contains scan data with corresponding nitrogen uptake average and status properties.
   * @return Returns the lowes growth stage (EC) value for fields with a green
   * scan data status, or undefined if no valid values are found in the input.
   * @public
   */
  public getGrowthStageFromScans(selectedFields: INFertilizationPlanFieldToSelectGridItem[]): number | undefined {
    if (!selectedFields || selectedFields.length === 0) {
      return undefined;
    }
    return selectedFields.filter(f => f.ScanData?.Status?.state === ApColumnState.Green)
      .map(f => f.ScanData.Ec)
      .filter(x => Number.isFinite(x))
      .reduce((min, current) => (!Number.isFinite(min) || current < min) ? current : min, undefined);
  }

  /**
   * Calculates and returns the threshold value based on the growth stage, crop key, and average nitrogen minimum values.
   * @param growStage - The growth stage of the crop. If not a finite number, the method will return undefined.
   * @param cropKey - The key of the crop for which the threshold is to be determined. If not a finite number, the method will return undefined.
   * @param avgNMin - The average nitrogen minimum value. This is optional and may influence the result if provided.
   * @return The calculated threshold value rounded to the nearest integer. Returns undefined if inputs are invalid or thresholds cannot be determined.
   * @public
   */
  public getThreshold(growStage: number | undefined, cropKey: number | undefined, avgNMin: number | undefined): number | undefined {
    let result = 0;
    if (!Number.isFinite(growStage) || !Number.isFinite(cropKey)) {
      return undefined;
    }
    const sensorCrop = this.cropV2CrStore.Crops.find((c) => c.CropKey === cropKey && c.CropType?.SensorCrop != null)?.CropType?.SensorCrop;
    if (!sensorCrop) {
      return undefined;
    }
    const thresholds = JSON.parse(sensorCrop.Threshold);
    const keys = Object.keys(thresholds);
    if (keys.length <= 0) {
      return undefined;
    }
    if (keys.length === 1) {
      return thresholds[keys[0]];
    }
    const firstKey = keys[0];
    const lastKey = keys[keys.length - 1];
    if (+firstKey > growStage) {
      result = thresholds[firstKey];
    } else if (+lastKey < growStage) {
      result = thresholds[lastKey];
    } else {
      result = thresholds[growStage.toString()];
    }
    if (avgNMin !== undefined) {
      result = Math.min(result, avgNMin * 0.5);
    }
    return this.roundNumericService.roundAsNumber(result, 0);
  }

  /**
   * Retrieves the crop type ID for a given field ID and campaign year from the crop rotation history.
   * @param fieldId The unique identifier of the field. If undefined, the crop type ID cannot be determined.
   * @param campaignYear The campaign year object containing the year. If undefined, the crop type ID cannot be determined.
   * @param cropRotationHistory The crop rotation history object that maps field IDs to crop rotation data. If undefined, the crop type ID cannot be determined.
   * @return The crop type ID if all inputs are valid and relevant data exists; otherwise, undefined.
   * @public
   */
  public getFarmCropId(fieldId: IGuid | undefined, campaignYear: ICampaignYear | undefined,
                       cropRotationHistory: FieldCropRotationHistory | undefined): System.IGuid | undefined {
    if (!fieldId || !campaignYear || !cropRotationHistory) {
      return undefined;
    }
    const fieldCrop = cropRotationHistory[fieldId.toString()];
    if (!fieldCrop) {
      return undefined;
    }
    return fieldCrop[campaignYear.Year]?.FarmCropId;
  }

  /**
   * Determines the default sensor crop filter based on the provided application mode, plan books,
   * existing selected fields, campaign year, and field crop rotation history.
   * @param applicationMode The mode of the application, which controls the default filter logic.
   * @param planBooks A list of fertilization plan books containing field-related data.
   * @param existingSelectedFields An array of selected fields tied to fertilization plans.
   * @param campaignYear The current campaign year used for crop type selection logic.
   * @param cropRotationHistory Historical crop rotation data associated with specific fields.
   * @return An array of boolean values indicating whether the default sensor crop filter condition is met.
   * @public
   */
  public getDefaultFilterIsSensorCrop(applicationMode: ApplicationModesEnum,
                                      planBooks: INFertilizationPlanBook[] | undefined,
                                      existingSelectedFields: INFertilizationPlanFieldToSelectGridItem[] | undefined,
                                      campaignYear: ICampaignYear | undefined,
                                      cropRotationHistory: FieldCropRotationHistory | undefined): boolean[] {
    const defaultFilter = [true, false];
    if (applicationMode === ApplicationModesEnum.Constant ||
      existingSelectedFields.length > 0 && existingSelectedFields.some(x => x.ScanData?.Status?.state !== 1)) {
      return defaultFilter;
    }
    const fieldIds = planBooks?.map(x => x.FieldId).filter(x => !!x) ?? [];
    for (const fieldId of fieldIds) {
      const farmCropId = this.getFarmCropId(fieldId, campaignYear, cropRotationHistory);
      if (!farmCropId) {
        continue;
      }
      const farmCrop = this.farmCropV2CrStore.findById(farmCropId);
      if (!farmCrop?.Crop?.CropType?.SensorCrop) {
        return defaultFilter;
      }
    }
    return [true];
  }
}
